import React, { useState, useEffect, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useGoogleAds } from 'hooks/useGoogleAds';
import { toast } from 'react-hot-toast';
import GoogleIcon from 'components/ui/icons/GoogleIcon';
import GenericModal, { NON_DISMISSIBLE_MODAL_PROPS } from './GenericModal';
import { Search } from 'lucide-react';
import { ModalsContext } from '../context/ModalsContext';
import { AuthContext } from '../context/AuthContext';
import { objetivosQueryKeys, fetchObjetivosForBrand } from 'queries/objetivos';
import { hasPlatformObjetivos } from 'utils/objetivosUtils';

const DROPDOWN_PORTAL_ID = 'google-ads-dropdown-portal';

const DROPDOWN_PORTAL_Z_INDEX = 11000;

const DropdownPortal = ({ children }) => {
  const [portalNode, setPortalNode] = useState(null);
  useEffect(() => {
    let node = document.getElementById(DROPDOWN_PORTAL_ID);
    if (!node) {
      node = document.createElement('div');
      node.id = DROPDOWN_PORTAL_ID;
      node.style.cssText = `position: fixed; inset: 0; pointer-events: none; z-index: ${DROPDOWN_PORTAL_Z_INDEX};`;
      document.body.appendChild(node);
    }
    setPortalNode(node);
    return () => {
      if (node && node.childNodes.length === 0) {
        node.remove();
      }
    };
  }, []);
  return portalNode ? createPortal(children, portalNode) : null;
};

/**
 * ConnectGoogleAdsAccountModal Component
 * 
 * Modal that allows users to select from available Google Ads accounts
 * after successful OAuth connection.
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 */
const ConnectGoogleAdsAccountModal = ({ isOpen, onClose }) => {
  const { openCreateObjectivesSetupModal } = useContext(ModalsContext);
  const { auth } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { accounts, fetchAccounts, setActiveGoogleAccount, loadingAccounts } = useGoogleAds();
  const [selectedAccount, setSelectedAccount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const dropdownBtnRef = useRef();
  const dropdownContentRef = useRef();
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Fetch accounts when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAccounts();
      setSelectedAccount('');
      setIsDropdownOpen(false);
      setAccountSearchQuery('');
    }
  }, [isOpen, fetchAccounts]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isDropdownOpen) setAccountSearchQuery('');
  }, [isDropdownOpen]);

  // Filter accounts by search (descriptive_name, customer_id)
  const filteredAccounts = accountSearchQuery.trim()
    ? accounts.filter(acc => {
        const q = accountSearchQuery.toLowerCase().trim();
        return (acc.descriptive_name || '').toLowerCase().includes(q) ||
               (acc.customer_id || '').toLowerCase().includes(q);
      })
    : accounts;

  // Outside click to close dropdown
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClick = (e) => {
      const insideButton = dropdownBtnRef.current?.contains(e.target);
      const insideDropdown = dropdownContentRef.current?.contains(e.target);
      if (!insideButton && !insideDropdown) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen && dropdownBtnRef.current) {
      const rect = dropdownBtnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isDropdownOpen]);

  const handleAccountSelect = (account) => {
    setSelectedAccount(account.customer_id);
    setIsDropdownOpen(false);
  };

  const handleConfirm = async () => {
    if (!selectedAccount) {
      toast.error('Por favor selecciona una cuenta de Google Ads');
      return;
    }

    const activeBrandId = auth.user?.active_brand_id;

    setIsSubmitting(true);
    try {
      await setActiveGoogleAccount(selectedAccount);
      toast.success('Cuenta de Google Ads seleccionada exitosamente');
      onClose();

      // If the brand already has Google objetivos (e.g. backend disconnect/reconnect
      // case), skip the mandatory setup modal so the user lands back on their existing
      // objetivos and optimization suggestions.
      let skipSetupModal = false;
      if (activeBrandId != null) {
        try {
          const objetivos = await queryClient.fetchQuery({
            queryKey: objetivosQueryKeys.list(String(activeBrandId)),
            queryFn: () => fetchObjetivosForBrand(String(activeBrandId)),
            staleTime: 2 * 60 * 1000,
          });
          skipSetupModal = hasPlatformObjetivos(objetivos, 'GOOGLE');
        } catch (err) {
          console.error('Error checking objetivos before opening setup modal:', err);
          // Fail open: keep current behavior so onboarding never silently stalls.
        }
      }

      if (!skipSetupModal) {
        openCreateObjectivesSetupModal({
          platform: 'GOOGLE',
          mandatoryUntilGenerated: true,
        });
      }
    } catch (error) {
      console.error('Error setting active account:', error);
      toast.error('Error al seleccionar la cuenta de Google Ads');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedAccountData = accounts.find(acc => acc.customer_id === selectedAccount);

  const isAccountDisabled = (account) => account.status !== 'ENABLED' || account.is_manager;
  const getAccountTooltip = (account) => {
    if (account.is_manager) {
      return 'Esta cuenta es una cuenta manager, por lo que no puede ser usada para operar.';
    }
    if (account.status !== 'ENABLED') {
      return 'Esta cuenta aún está en proceso de creación. Ingresa a ads.google.com para finalizarlo.';
    }
    return '';
  };

  const isSelectedAccountValid = selectedAccount && (() => {
    const acc = accounts.find(a => a.customer_id === selectedAccount);
    return acc && !isAccountDisabled(acc);
  })();

  return (
    <GenericModal
      isOpen={isOpen}
      toggle={onClose}
      {...NON_DISMISSIBLE_MODAL_PROPS}
      titleSlot={
        <>
          <GoogleIcon width={22} height={22} />
          <span className="ml-2 text-base font-semibold text-white">Elegí una Cuenta de Google Ads</span>
        </>
      }
      description="¿Tu negocio tiene cuenta de Google Ads? Seleccionala"
      onSubmit={handleConfirm}
      onClose={onClose}
      isSubmitting={isSubmitting}
      submitDisabled={!isSelectedAccountValid}
      showLogo={true}
      width="520px"
      contentPadding="1.5rem 2rem 2rem 2rem"
      showSubmitButton={true}
    >
      {loadingAccounts ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Cargando cuentas...</p>
        </div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600">No se encontraron cuentas de Google Ads</p>
        </div>
      ) : (
        <>
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cuenta de Google Ads
            </label>
            <div className="relative">
              <button
                type="button"
                ref={dropdownBtnRef}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={isSubmitting}
                className="w-full px-4 py-3 text-left bg-white border-2 border-blue-400 rounded-xl shadow-sm hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between text-lg"
                style={{ minHeight: 56 }}
              >
                <div className="flex items-center">
                  {selectedAccountData ? (
                    <>
                      <div>
                        <div className="font-medium text-gray-900">
                          {selectedAccountData.descriptive_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {selectedAccountData.customer_id}
                        </div>
                      </div>
                    </>
                  ) : (
                    <span className="text-gray-500">Seleccioná una cuenta</span>
                  )}
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isDropdownOpen && (
                <DropdownPortal>
                  <div
                    ref={dropdownContentRef}
                    className="bg-white border border-blue-400 rounded-xl shadow-2xl overflow-hidden"
                    style={{
                      position: 'fixed',
                      top: dropdownPos.top,
                      left: dropdownPos.left,
                      width: dropdownPos.width,
                      minWidth: 240,
                      padding: 0,
                      pointerEvents: 'auto',
                    }}
                  >
                    <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={accountSearchQuery}
                          onChange={(e) => setAccountSearchQuery(e.target.value)}
                          onMouseDown={(e) => e.stopPropagation()}
                          placeholder="Buscar cuenta por nombre o ID..."
                          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-auto py-2">
                    {filteredAccounts.length === 0 ? (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">
                        No se encontraron cuentas
                      </div>
                    ) : (
                    filteredAccounts.map((account) => {
                      const disabled = isAccountDisabled(account);
                      const tooltip = getAccountTooltip(account);
                      return (
                        <button
                          key={account.customer_id}
                          onClick={() => !disabled && handleAccountSelect(account)}
                          disabled={disabled}
                          className={`w-full px-4 py-3 text-left flex items-center border-b border-gray-100 last:border-b-0 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-50 focus:bg-blue-50 focus:outline-none'}`}
                          title={disabled ? tooltip : ''}
                        >
                          <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center mr-3">
                            <GoogleIcon width={16} height={16} />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {account.descriptive_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {account.customer_id}
                            </div>
                          </div>
                        </button>
                      );
                    })
                    )}
                    </div>
                  </div>
                </DropdownPortal>
              )}
            </div>
          </div>
        </>
      )}
    </GenericModal>
  );
};

export default ConnectGoogleAdsAccountModal;
